import { doc, getDoc, writeBatch } from "firebase/firestore";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import {
  appendContentBoxIdsToOrderBatch,
  mergeLocalContentBoxesOrder,
  mergeOrderedIdsIntoContentBoxesOrder,
  replaceContentBoxesOrderBatchAfterFreshRead,
} from "./contentBoxOrderAppend";
import { coversMultipleDays } from "./activeDatesMultiDayDetection";
import { buildPrintViewDateDataFromKey } from "./printViewDateFilter";
import {
  estimateCalendarCopyWritesByClass,
  getBatchLimitBreach,
} from "./presentCalendarBatchLimits";
import { regenerateNestedContentBoxIds } from "./contentBoxNestedIdentity";

export const CALENDAR_COPY_ACTIVE_DATE_MODES = {
  EXPLODE: "explode",
  SHIFT: "shift",
  EXTEND: "extend",
  EXCLUDE: "exclude",
};

const WEEKDAY_NAMES = [
  "Sundays",
  "Mondays",
  "Tuesdays",
  "Wednesdays",
  "Thursdays",
  "Fridays",
  "Saturdays",
];

const getContentBoxId = (box) => box?.contentBoxId ?? box?.id;

const getUniqueBoxKey = (box) => {
  const boxId = getContentBoxId(box);
  return boxId ? String(boxId) : null;
};

const FALLBACK_COPY_RANK = Number.MAX_SAFE_INTEGER;

const buildCopyWriteOrder = ({
  sourceRank = FALLBACK_COPY_RANK,
  targetDateRank = FALLBACK_COPY_RANK,
  sequence = FALLBACK_COPY_RANK,
} = {}) => ({
  sourceRank,
  targetDateRank,
  sequence,
});

const compareCopyWriteOrder = (left = {}, right = {}) => {
  const leftOrder = left.copyOrder || {};
  const rightOrder = right.copyOrder || {};
  const leftSourceRank = leftOrder.sourceRank ?? FALLBACK_COPY_RANK;
  const rightSourceRank = rightOrder.sourceRank ?? FALLBACK_COPY_RANK;
  if (leftSourceRank !== rightSourceRank) return leftSourceRank - rightSourceRank;

  const leftTargetDateRank = leftOrder.targetDateRank ?? FALLBACK_COPY_RANK;
  const rightTargetDateRank = rightOrder.targetDateRank ?? FALLBACK_COPY_RANK;
  if (leftTargetDateRank !== rightTargetDateRank) return leftTargetDateRank - rightTargetDateRank;

  const leftSequence = leftOrder.sequence ?? FALLBACK_COPY_RANK;
  const rightSequence = rightOrder.sequence ?? FALLBACK_COPY_RANK;
  return leftSequence - rightSequence;
};

export const sortCalendarCopyWrites = (writes = []) =>
  [...(Array.isArray(writes) ? writes : [])].sort(compareCopyWriteOrder);

const isDateKey = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const stampNestedCalendarDatesForCopy = (box = {}, activeDates = {}) => {
  if (!box?.multiBox || !Array.isArray(box.content) || !isDateKey(activeDates.startDate)) {
    return box.content;
  }

  return box.content.map((nestedBox) => {
    if (!isDateKey(nestedBox?.startDate)) {
      return nestedBox;
    }

    return {
      ...nestedBox,
      startDate: activeDates.startDate,
      endDate: isDateKey(activeDates.endDate) ? activeDates.endDate : activeDates.startDate,
    };
  });
};

const buildCalendarCopyPayload = (box, newContentBoxId, activeDates = {}, createId) => {
  const payload = regenerateNestedContentBoxIds({
    ...box,
    id: newContentBoxId,
    contentBoxId: newContentBoxId,
    startDate: activeDates.startDate,
    endDate: activeDates.endDate,
  }, createId);
  const stampedContent = stampNestedCalendarDatesForCopy(payload, activeDates);
  if (stampedContent !== payload.content) {
    payload.content = stampedContent;
  }
  return payload;
};

export function shiftDateKey(dateKey, dayOffset = 0) {
  if (!isDateKey(dateKey)) return dateKey;
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(year, month - 1, day + dayOffset);
  return date.toLocaleDateString("en-CA");
}

export function getDateKeyOffset(sourceDateKey, targetDateKey) {
  if (!isDateKey(sourceDateKey) || !isDateKey(targetDateKey)) return 0;
  const [sourceYear, sourceMonth, sourceDay] = sourceDateKey.split("-").map(Number);
  const [targetYear, targetMonth, targetDay] = targetDateKey.split("-").map(Number);
  const sourceDate = new Date(sourceYear, sourceMonth - 1, sourceDay);
  const targetDate = new Date(targetYear, targetMonth - 1, targetDay);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return Math.round((targetDate - sourceDate) / millisecondsPerDay);
}

const shiftWeekdayName = (weekdayName, dayOffset = 0) => {
  const index = WEEKDAY_NAMES.indexOf(weekdayName);
  if (index === -1) return weekdayName;
  const nextIndex = ((index + dayOffset) % WEEKDAY_NAMES.length + WEEKDAY_NAMES.length) % WEEKDAY_NAMES.length;
  return WEEKDAY_NAMES[nextIndex];
};

const dedupeWeekdayNames = (weekdayNames = []) => {
  const seen = new Set();
  return weekdayNames.filter((weekdayName) => {
    if (!WEEKDAY_NAMES.includes(weekdayName) || seen.has(weekdayName)) return false;
    seen.add(weekdayName);
    return true;
  });
};

export function shiftActiveDates(activeDates = {}, dayOffset = 0) {
  const { startDate, endDate } = activeDates;
  if (Array.isArray(startDate)) {
    return {
      startDate: dedupeWeekdayNames(startDate),
      endDate: "",
    };
  }

  const shiftedStartDate = shiftDateKey(startDate, dayOffset);
  const shiftedEndDate = shiftDateKey(endDate || startDate, dayOffset);
  return {
    startDate: shiftedStartDate,
    endDate: shiftedEndDate,
  };
}

export function extendActiveDates(activeDates = {}, dayOffset = 0) {
  const shiftedDates = shiftActiveDates(activeDates, dayOffset);
  if (Array.isArray(activeDates.startDate)) {
    return {
      startDate: dedupeWeekdayNames([
        ...activeDates.startDate,
        ...activeDates.startDate.map((weekdayName) => shiftWeekdayName(weekdayName, dayOffset)),
      ]),
      endDate: "",
    };
  }

  const originalStartDate = activeDates.startDate;
  const originalEndDate = activeDates.endDate || originalStartDate;
  const dateKeys = [originalStartDate, originalEndDate, shiftedDates.startDate, shiftedDates.endDate].filter(isDateKey);
  if (dateKeys.length === 0) {
    return shiftedDates;
  }

  return {
    startDate: dateKeys.reduce((minDate, dateKey) => (dateKey < minDate ? dateKey : minDate), dateKeys[0]),
    endDate: dateKeys.reduce((maxDate, dateKey) => (dateKey > maxDate ? dateKey : maxDate), dateKeys[0]),
  };
}

export function collectUniqueBoxesFromCopyPairs(copyPairs = []) {
  const boxesById = new Map();
  (copyPairs || []).forEach((pair) => {
    (pair?.boxes || []).forEach((box) => {
      const boxKey = getUniqueBoxKey(box);
      if (!boxKey || boxesById.has(boxKey)) return;
      boxesById.set(boxKey, box);
    });
  });
  return Array.from(boxesById.values());
}

export function partitionBoxesByActiveDateSpan(boxes = []) {
  return (boxes || []).reduce(
    (partitioned, box) => {
      if (coversMultipleDays({ startDate: box?.startDate, endDate: box?.endDate })) {
        partitioned.multiDay.push(box);
      } else {
        partitioned.singleDay.push(box);
      }
      return partitioned;
    },
    { singleDay: [], multiDay: [] }
  );
}

export function hasMultiDayBoxes(boxes = []) {
  return partitionBoxesByActiveDateSpan(boxes).multiDay.length > 0;
}

/**
 * Absolute calendar date label for copy prompts (no Today/Yesterday/Tomorrow).
 * Example: "Thursday, August 6th, 2026"
 */
export function formatCalendarCopyAbsoluteDateLabel(dateKey) {
  const dateData = buildPrintViewDateDataFromKey(dateKey);
  if (!dateData?.dayName || !dateData?.dateString) {
    return String(dateKey || "");
  }
  return `${dateData.dayName}, ${dateData.dateString}`;
}

export function formatCalendarCopyActiveDatesRangeLabel(activeDates = {}) {
  if (Array.isArray(activeDates.startDate)) {
    const weekdays = activeDates.startDate.filter(Boolean);
    if (weekdays.length === 0) return "";
    if (weekdays.length === 1) return weekdays[0];
    if (weekdays.length === 2) return `${weekdays[0]} and ${weekdays[1]}`;
    return `${weekdays.slice(0, -1).join(", ")}, and ${weekdays[weekdays.length - 1]}`;
  }

  const startDate = activeDates.startDate;
  const endDate = activeDates.endDate || startDate;
  if (!startDate) return "";
  if (!endDate || String(startDate) === String(endDate)) {
    return formatCalendarCopyAbsoluteDateLabel(startDate);
  }
  return `${formatCalendarCopyAbsoluteDateLabel(startDate)} – ${formatCalendarCopyAbsoluteDateLabel(endDate)}`;
}

export function buildActiveDatesFromDateKeys(dateKeys = [], fallbackDateKey = null) {
  const sortedKeys = [...new Set((dateKeys || []).filter(isDateKey))].sort();
  if (sortedKeys.length > 0) {
    return {
      startDate: sortedKeys[0],
      endDate: sortedKeys[sortedKeys.length - 1],
    };
  }
  if (isDateKey(fallbackDateKey)) {
    return {
      startDate: fallbackDateKey,
      endDate: fallbackDateKey,
    };
  }
  return null;
}

/**
 * Merge destination calendar date keys into an existing active-date range.
 * Used by EXTEND so the original box also becomes active on the paste target day/week.
 */
export function mergeActiveDatesWithDateKeys(activeDates = {}, dateKeys = []) {
  const destinationDates = buildActiveDatesFromDateKeys(dateKeys);
  if (!destinationDates) {
    return {
      startDate: activeDates.startDate,
      endDate: activeDates.endDate || activeDates.startDate || "",
    };
  }

  if (Array.isArray(activeDates.startDate)) {
    const weekdayNames = [...new Set(
      (dateKeys || [])
        .filter(isDateKey)
        .map((dateKey) => {
          const [year, month, day] = dateKey.split("-").map(Number);
          return WEEKDAY_NAMES[new Date(year, month - 1, day).getDay()];
        })
        .filter(Boolean)
    )];
    return {
      startDate: dedupeWeekdayNames([...(activeDates.startDate || []), ...weekdayNames]),
      endDate: "",
    };
  }

  const originalStartDate = activeDates.startDate;
  const originalEndDate = activeDates.endDate || originalStartDate;
  const allKeys = [originalStartDate, originalEndDate, destinationDates.startDate, destinationDates.endDate]
    .filter(isDateKey);
  if (allKeys.length === 0) {
    return destinationDates;
  }

  return {
    startDate: allKeys.reduce((minDate, dateKey) => (dateKey < minDate ? dateKey : minDate), allKeys[0]),
    endDate: allKeys.reduce((maxDate, dateKey) => (dateKey > maxDate ? dateKey : maxDate), allKeys[0]),
  };
}

/**
 * Destination active-date ranges SHIFT will apply for multi-day boxes in a copy prompt:
 * the paste target day (or span of target days for a week copy), not a shifted original range.
 */
export function collectCalendarCopyMultiDayDestinationActiveDates(
  copyRequest = {},
  { groupByDestination = false } = {}
) {
  const boxesByKey = new Map();

  (copyRequest.copyPairs || []).forEach((pair) => {
    (pair?.boxes || []).forEach((box) => {
      if (!coversMultipleDays({ startDate: box?.startDate, endDate: box?.endDate })) return;
      const boxKey = getUniqueBoxKey(box);
      if (!boxKey) return;
      const destinationGroupKey =
        groupByDestination && pair?.destinationGroupKey
          ? String(pair.destinationGroupKey)
          : "all-destinations";
      const groupedBoxKey = `${boxKey}::${destinationGroupKey}`;
      if (!boxesByKey.has(groupedBoxKey)) {
        boxesByKey.set(groupedBoxKey, []);
      }
      const targetDateKeys = boxesByKey.get(groupedBoxKey);
      if (pair?.targetDateKey && !targetDateKeys.includes(pair.targetDateKey)) {
        targetDateKeys.push(pair.targetDateKey);
      }
    });
  });

  return Array.from(boxesByKey.values()).map((targetDateKeys) =>
    buildActiveDatesFromDateKeys(targetDateKeys, copyRequest.targetAnchorDateKey)
  ).filter(Boolean);
}

/**
 * Human-readable label for the destination active dates a multi-day copy option will apply.
 */
export function formatCalendarCopyShiftedMultiDayDatesLabel(
  copyRequest = {},
  { groupByDestination = false } = {}
) {
  const labels = [];
  const seen = new Set();

  collectCalendarCopyMultiDayDestinationActiveDates(copyRequest, { groupByDestination }).forEach((activeDates) => {
    const label = formatCalendarCopyActiveDatesRangeLabel(activeDates);
    if (!label || seen.has(label)) return;
    seen.add(label);
    labels.push(label);
  });

  if (labels.length === 0) return "the new dates";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}


export function hasMultiDayCopyPairs(copyPairs = []) {
  return hasMultiDayBoxes(collectUniqueBoxesFromCopyPairs(copyPairs));
}

export const CALENDAR_COPY_ERROR =
  "Unable to copy boxes. Please check your connection and try again.";

export const CALENDAR_COPY_WEEK_ERROR =
  "Unable to copy the week. Some boxes may not have been copied. Please refresh and try again.";

export const CALENDAR_COPY_PARTIAL_WRITE_ERROR =
  "Some boxes may have been created but could not be added to the calendar. Please refresh before trying again.";

export const CALENDAR_COPY_MISSING_CONTEXT_ERROR =
  "Unable to copy boxes because your class session is missing. Please refresh and try again.";

export const CALENDAR_COPY_NOTHING_TO_COPY_ERROR =
  "No boxes were copied because all selected boxes were excluded.";

export const CALENDAR_COPY_TOO_LARGE_ERROR =
  "This copy is too large to complete safely in one step. Select fewer boxes, fewer days, or fewer classes and try again.";

const getUniqueClassIds = (classIds = []) => [...new Set((classIds || []).filter(Boolean))];

export function resolveCopyPairTargetClassIds({
  baseTargetClassIds = [],
  sourceClassId,
  sourceAnchorKey,
  targetAnchorKey,
} = {}) {
  const uniqueClassIds = getUniqueClassIds(baseTargetClassIds);
  if (!sourceClassId || !sourceAnchorKey || !targetAnchorKey) return uniqueClassIds;
  if (String(sourceAnchorKey) !== String(targetAnchorKey)) return uniqueClassIds;
  return uniqueClassIds.filter((targetClassId) => targetClassId !== sourceClassId);
}

export function resolveCalendarCopyErrorMessage({ partialWrite = false, isWeekCopy = false } = {}) {
  if (partialWrite) {
    return CALENDAR_COPY_PARTIAL_WRITE_ERROR;
  }
  if (isWeekCopy) {
    return CALENDAR_COPY_WEEK_ERROR;
  }
  return CALENDAR_COPY_ERROR;
}

export async function planCalendarCopyToDate({
  boxes,
  targetDateKey,
  targetClassIds,
  sourceClassId,
  localClassId = sourceClassId,
  userId,
  classes,
  resolveLinkedClassIdsFn,
  createId,
}) {
  const nextOverrides = {};
  const writesByClass = {};
  const layoutCopyEntries = [];
  const uniqueClassIds = getUniqueClassIds(targetClassIds);
  const linkedClassIdsCache = new Map();

  const getLinkedClassIdsForBox = async (box) => {
    const sourceContentBoxId = box?.contentBoxId ?? box?.id;
    if (!box?.linkedBox || !sourceContentBoxId || !userId) {
      return [];
    }
    const cacheKey = String(sourceContentBoxId);
    if (linkedClassIdsCache.has(cacheKey)) {
      return linkedClassIdsCache.get(cacheKey);
    }
    const classIds = (classes || []).map((cls) => cls.classId).filter(Boolean);
    const linkedClassIds = await resolveLinkedClassIdsFn(userId, String(sourceContentBoxId), {
      sourceClassId,
      classIds,
      hintLinkedClassIds: box?.linkedClassIds,
    });
    linkedClassIdsCache.set(cacheKey, linkedClassIds);
    return linkedClassIds;
  };

  let writeSequence = 0;

  const appendWrite = (targetClassId, newContentBoxId, box, copyOrder, payload) => {
    const copyPayload = payload || buildCalendarCopyPayload(box, newContentBoxId, {
      startDate: targetDateKey,
      endDate: targetDateKey,
    }, createId);
    if (!writesByClass[targetClassId]) {
      writesByClass[targetClassId] = [];
    }
    writesByClass[targetClassId].push({
      contentBoxId: newContentBoxId,
      copyOrder,
      payload: copyPayload,
    });
    layoutCopyEntries.push({
      copyOrder,
      sourceBox: box,
      targetBox: copyPayload,
      sourceClassId,
      sourceContentBoxId: getContentBoxId(box),
      targetClassId,
      targetContentBoxId: newContentBoxId,
      targetDateKey,
    });

    if (targetClassId === localClassId) {
      nextOverrides[newContentBoxId] = {
        startDate: targetDateKey,
        endDate: targetDateKey,
      };
    }
  };

  for (const [sourceRank, box] of (boxes || []).entries()) {
    const copyOrder = buildCopyWriteOrder({
      sourceRank,
      targetDateRank: 0,
      sequence: writeSequence++,
    });
    if (box?.linkedBox) {
      const newContentBoxId = createId();
      const payload = buildCalendarCopyPayload(box, newContentBoxId, {
        startDate: targetDateKey,
        endDate: targetDateKey,
      }, createId);
      const linkedClassIds = await getLinkedClassIdsForBox(box);
      const expandedTargetClassIds = [
        ...new Set([...uniqueClassIds, ...linkedClassIds].filter(Boolean)),
      ];
      for (const targetClassId of expandedTargetClassIds) {
        appendWrite(targetClassId, newContentBoxId, box, copyOrder, payload);
      }
    } else {
      for (const targetClassId of uniqueClassIds) {
        const newContentBoxId = createId();
        appendWrite(targetClassId, newContentBoxId, box, copyOrder);
      }
    }
  }

  return { layoutCopyEntries, nextOverrides, writesByClass };
}

export async function planCalendarCopyWithActiveDateMode({
  copyPairs,
  activeDateMode = CALENDAR_COPY_ACTIVE_DATE_MODES.EXPLODE,
  sourceAnchorDateKey,
  targetAnchorDateKey,
  targetClassIds,
  sourceClassId,
  localClassId = sourceClassId,
  userId,
  classes,
  resolveLinkedClassIdsFn,
  createId,
}) {
  const nextOverrides = {};
  const writesByClass = {};
  const layoutCopyEntries = [];
  const updatesByClass = {};
  const uniqueClassIds = getUniqueClassIds(targetClassIds);
  const linkedClassIdsCache = new Map();
  const writtenPerTargetDay = new Set();
  const uniqueMultiDayBoxes = new Map();
  let writeSequence = 0;

  const getLinkedClassIdsForBox = async (box) => {
    const sourceContentBoxId = getContentBoxId(box);
    if (!box?.linkedBox || !sourceContentBoxId || !userId) {
      return [];
    }
    const cacheKey = String(sourceContentBoxId);
    if (linkedClassIdsCache.has(cacheKey)) {
      return linkedClassIdsCache.get(cacheKey);
    }
    const classIds = (classes || []).map((cls) => cls.classId).filter(Boolean);
    const linkedClassIds = await resolveLinkedClassIdsFn(userId, String(sourceContentBoxId), {
      sourceClassId,
      classIds,
      hintLinkedClassIds: box?.linkedClassIds,
    });
    linkedClassIdsCache.set(cacheKey, linkedClassIds);
    return linkedClassIds;
  };

  const appendWrite = (
    targetClassId,
    newContentBoxId,
    box,
    activeDates,
    copyOrder,
    targetDateKeys = [],
    payload
  ) => {
    const copyPayload = payload || buildCalendarCopyPayload(box, newContentBoxId, activeDates, createId);
    if (!writesByClass[targetClassId]) {
      writesByClass[targetClassId] = [];
    }
    writesByClass[targetClassId].push({
      contentBoxId: newContentBoxId,
      copyOrder,
      payload: copyPayload,
    });
    targetDateKeys.forEach((targetDateKey) => {
      layoutCopyEntries.push({
        copyOrder,
        sourceBox: box,
        targetBox: copyPayload,
        sourceClassId,
        sourceContentBoxId: getContentBoxId(box),
        targetClassId,
        targetContentBoxId: newContentBoxId,
        targetDateKey,
      });
    });

    if (targetClassId === localClassId) {
      nextOverrides[newContentBoxId] = {
        startDate: activeDates.startDate,
        endDate: activeDates.endDate,
      };
    }
  };

  const appendUpdate = (targetClassId, contentBoxId, activeDates, previousActiveDates = {}) => {
    if (!targetClassId || !contentBoxId) return;
    if (!updatesByClass[targetClassId]) {
      updatesByClass[targetClassId] = [];
    }
    updatesByClass[targetClassId].push({
      contentBoxId,
      updates: {
        startDate: activeDates.startDate,
        endDate: activeDates.endDate,
      },
      previous: {
        startDate: previousActiveDates.startDate ?? "",
        endDate: previousActiveDates.endDate ?? previousActiveDates.startDate ?? "",
      },
    });

    if (targetClassId === localClassId) {
      nextOverrides[contentBoxId] = {
        startDate: activeDates.startDate,
        endDate: activeDates.endDate,
      };
    }
  };

  const appendCopyForBox = async (
    box,
    activeDates,
    copyOrder,
    targetDateKeys = [],
    targetClassIdsForCopy = uniqueClassIds,
    excludedTargetClassIds = []
  ) => {
    const resolvedTargetClassIds = getUniqueClassIds(targetClassIdsForCopy);
    const excludedClassIds = new Set(excludedTargetClassIds.filter(Boolean));
    if (box?.linkedBox) {
      const newContentBoxId = createId();
      const payload = buildCalendarCopyPayload(box, newContentBoxId, activeDates, createId);
      const linkedClassIds = await getLinkedClassIdsForBox(box);
      const expandedTargetClassIds = [
        ...new Set([...resolvedTargetClassIds, ...linkedClassIds].filter(Boolean)),
      ].filter((targetClassId) => !excludedClassIds.has(targetClassId));
      for (const targetClassId of expandedTargetClassIds) {
        appendWrite(targetClassId, newContentBoxId, box, activeDates, copyOrder, targetDateKeys, payload);
      }
      return;
    }

    for (const targetClassId of resolvedTargetClassIds) {
      if (excludedClassIds.has(targetClassId)) continue;
      const newContentBoxId = createId();
      appendWrite(targetClassId, newContentBoxId, box, activeDates, copyOrder, targetDateKeys);
    }
  };

  for (const [targetDateRank, pair] of (copyPairs || []).entries()) {
    const pairTargetClassIds = getUniqueClassIds(pair?.targetClassIds || uniqueClassIds);
    if (pairTargetClassIds.length === 0) continue;
    const excludedTargetClassIds = uniqueClassIds.filter(
      (targetClassId) => !pairTargetClassIds.includes(targetClassId)
    );

    for (const [sourceRank, box] of (pair?.boxes || []).entries()) {
      const boxKey = getUniqueBoxKey(box);
      if (!boxKey) continue;

      const copyOrder = buildCopyWriteOrder({
        sourceRank,
        targetDateRank,
        sequence: writeSequence++,
      });
      const isMultiDayBox = coversMultipleDays({ startDate: box?.startDate, endDate: box?.endDate });
      if (isMultiDayBox && activeDateMode !== CALENDAR_COPY_ACTIVE_DATE_MODES.EXPLODE) {
        const multiDayGroupKey =
          activeDateMode === CALENDAR_COPY_ACTIVE_DATE_MODES.SHIFT && pair?.destinationGroupKey
            ? `${boxKey}::${String(pair.destinationGroupKey)}`
            : boxKey;
        if (!uniqueMultiDayBoxes.has(multiDayGroupKey)) {
          uniqueMultiDayBoxes.set(multiDayGroupKey, {
            box,
            copyOrder,
            targetDateKeys: [],
            targetClassIds: new Set(pairTargetClassIds),
            excludedTargetClassIds: new Set(excludedTargetClassIds),
          });
        }
        const multiDayBox = uniqueMultiDayBoxes.get(multiDayGroupKey);
        pairTargetClassIds.forEach((targetClassId) => multiDayBox.targetClassIds.add(targetClassId));
        multiDayBox.excludedTargetClassIds = new Set(
          [...multiDayBox.excludedTargetClassIds].filter((targetClassId) =>
            excludedTargetClassIds.includes(targetClassId)
          )
        );
        if (!multiDayBox.targetDateKeys.includes(pair.targetDateKey)) {
          multiDayBox.targetDateKeys.push(pair.targetDateKey);
        }
        continue;
      }

      const writeKey = `${boxKey}::${pair.targetDateKey}`;
      if (writtenPerTargetDay.has(writeKey)) continue;
      writtenPerTargetDay.add(writeKey);
      await appendCopyForBox(box, {
        startDate: pair.targetDateKey,
        endDate: pair.targetDateKey,
      }, copyOrder, [pair.targetDateKey], pairTargetClassIds, excludedTargetClassIds);
    }
  }

  if (activeDateMode === CALENDAR_COPY_ACTIVE_DATE_MODES.SHIFT) {
    for (const {
      box,
      copyOrder,
      targetDateKeys,
      targetClassIds: multiDayTargetClassIds,
      excludedTargetClassIds: multiDayExcludedTargetClassIds,
    } of uniqueMultiDayBoxes.values()) {
      const activeDates = buildActiveDatesFromDateKeys(targetDateKeys, targetAnchorDateKey);
      if (!activeDates) continue;
      await appendCopyForBox(
        box,
        activeDates,
        copyOrder,
        targetDateKeys,
        [...multiDayTargetClassIds],
        [...multiDayExcludedTargetClassIds]
      );
    }
  }

  if (activeDateMode === CALENDAR_COPY_ACTIVE_DATE_MODES.EXTEND) {
    for (const { box, copyOrder, targetDateKeys } of uniqueMultiDayBoxes.values()) {
      const contentBoxId = getContentBoxId(box);
      if (!contentBoxId) continue;
      const activeDates = mergeActiveDatesWithDateKeys(
        { startDate: box.startDate, endDate: box.endDate },
        targetDateKeys
      );
      const linkedClassIds = await getLinkedClassIdsForBox(box);
      const classIdsToUpdate = [
        ...new Set([sourceClassId, ...linkedClassIds].filter(Boolean)),
      ];
      classIdsToUpdate.forEach((targetClassId) => {
        appendUpdate(targetClassId, String(contentBoxId), activeDates, {
          startDate: box.startDate,
          endDate: box.endDate,
        });
        targetDateKeys.forEach((targetDateKey) => {
          layoutCopyEntries.push({
            copyOrder,
            sourceBox: box,
            sourceClassId,
            sourceContentBoxId: String(contentBoxId),
            targetClassId,
            targetContentBoxId: String(contentBoxId),
            targetDateKey,
          });
        });
      });
      const updateClassIds = new Set(classIdsToUpdate);
      uniqueClassIds
        .filter((targetClassId) => !updateClassIds.has(targetClassId))
        .forEach((targetClassId) => {
          appendWrite(targetClassId, createId(), box, activeDates, copyOrder, targetDateKeys);
        });
    }
  }

  const orderMergeSegmentsByClass = {};
  if (activeDateMode === CALENDAR_COPY_ACTIVE_DATE_MODES.EXTEND) {
    const targetIdByKey = new Map();
    layoutCopyEntries.forEach((entry) => {
      if (!entry?.targetClassId || !entry?.sourceContentBoxId || !entry?.targetDateKey) return;
      targetIdByKey.set(
        `${entry.targetClassId}::${entry.sourceContentBoxId}::${entry.targetDateKey}`,
        String(entry.targetContentBoxId)
      );
    });

    const classIdsForOrder = [...new Set([
      ...uniqueClassIds,
      sourceClassId,
      ...Object.keys(writesByClass),
      ...Object.keys(updatesByClass),
    ].filter(Boolean))];

    (copyPairs || []).forEach((pair) => {
      classIdsForOrder.forEach((targetClassId) => {
        const segment = [];
        (pair?.boxes || []).forEach((box) => {
          const sourceId = getContentBoxId(box);
          if (!sourceId) return;
          const mappedId = targetIdByKey.get(
            `${targetClassId}::${sourceId}::${pair.targetDateKey}`
          );
          if (mappedId) {
            segment.push(mappedId);
            return;
          }
          if (coversMultipleDays({ startDate: box.startDate, endDate: box.endDate })) {
            segment.push(String(sourceId));
          }
        });
        if (segment.length === 0) return;
        if (!orderMergeSegmentsByClass[targetClassId]) {
          orderMergeSegmentsByClass[targetClassId] = [];
        }
        orderMergeSegmentsByClass[targetClassId].push(segment);
      });
    });
  }

  return {
    layoutCopyEntries,
    nextOverrides,
    writesByClass,
    updatesByClass,
    orderMergeSegmentsByClass,
  };
}

export async function persistClassCopyBatch({
  db,
  userId,
  targetClassId,
  writes = [],
  updates = [],
  currentOrder,
  orderMergeSegments = [],
}) {
  const classRef = doc(
    db,
    FIREBASE_COLLECTIONS.USERS,
    userId,
    FIREBASE_COLLECTIONS.CLASSES,
    targetClassId
  );
  const batch = writeBatch(db);
  const createdIds = [];
  const orderedWrites = sortCalendarCopyWrites(writes);
  const normalizedOrderMergeSegments = (Array.isArray(orderMergeSegments) ? orderMergeSegments : [])
    .map((segment) => (Array.isArray(segment) ? segment.map(String).filter(Boolean) : []))
    .filter((segment) => segment.length > 0);
  const needsOrderMerge = normalizedOrderMergeSegments.length > 0;
  let previousOrder = Array.isArray(currentOrder) ? [...currentOrder] : [];

  try {
    const classSnap = await getDoc(classRef);
    if (Array.isArray(classSnap?.data?.()?.contentBoxesOrder)) {
      previousOrder = classSnap.data().contentBoxesOrder;
    }
  } catch {
    // Fall back to caller-provided order for undo metadata when the fresh read fails.
  }

  for (const write of orderedWrites) {
    const contentBoxRef = doc(classRef, FIREBASE_COLLECTIONS.CONTENT_BOXES, write.contentBoxId);
    batch.set(contentBoxRef, write.payload);
    createdIds.push(write.contentBoxId);
  }

  for (const update of updates) {
    const contentBoxRef = doc(classRef, FIREBASE_COLLECTIONS.CONTENT_BOXES, update.contentBoxId);
    batch.update(contentBoxRef, update.updates);
  }

  let nextOrder = mergeLocalContentBoxesOrder(previousOrder, createdIds);
  if (needsOrderMerge) {
    let serverOrder = [...previousOrder];

    nextOrder = [...serverOrder];
    normalizedOrderMergeSegments.forEach((segment) => {
      nextOrder = mergeOrderedIdsIntoContentBoxesOrder(nextOrder, segment);
    });
    nextOrder = mergeLocalContentBoxesOrder(nextOrder, createdIds);
    const orderChanged =
      nextOrder.length !== serverOrder.length ||
      nextOrder.some((id, index) => String(id) !== String(serverOrder[index]));
    if (orderChanged) {
      replaceContentBoxesOrderBatchAfterFreshRead({
        batch,
        classRef,
        nextOrder,
        serverOrder,
      });
    } else if (createdIds.length > 0) {
      appendContentBoxIdsToOrderBatch(batch, classRef, createdIds);
    }
  } else if (createdIds.length > 0) {
    appendContentBoxIdsToOrderBatch(batch, classRef, createdIds);
  }
  await batch.commit();

  try {
    const classSnap = await getDoc(classRef);
    if (classSnap?.exists?.()) {
      const serverOrder = classSnap.data()?.contentBoxesOrder;
      if (Array.isArray(serverOrder)) {
        nextOrder = serverOrder;
      }
    }
  } catch {
    // Best-effort Redux reconciliation only; a successful copy should not be relabeled as failed.
  }

  return {
    createdIds,
    nextOrder,
    previousOrder,
  };
}

export async function persistCalendarCopyPlan({
  db,
  userId,
  classes,
  writesByClass,
  updatesByClass = {},
  orderMergeSegmentsByClass = {},
}) {
  const batchLimitBreach = getBatchLimitBreach(estimateCalendarCopyWritesByClass({
    writesByClass,
    updatesByClass,
    orderMergeSegmentsByClass,
  }));
  if (!batchLimitBreach.ok) {
    return {
      ok: false,
      partialWrite: false,
      error: CALENDAR_COPY_TOO_LARGE_ERROR,
      batchLimitBreach,
      updatedClasses: [],
    };
  }

  const classById = new Map((classes || []).map((entry) => [entry.classId, entry]));
  const updatedClasses = [];
  const undoClasses = [];
  let changedDocCount = 0;
  const classIdsWithChanges = [
    ...new Set([
      ...Object.keys(writesByClass || {}),
      ...Object.keys(updatesByClass || {}),
      ...Object.keys(orderMergeSegmentsByClass || {}),
    ]),
  ];

  for (const targetClassId of classIdsWithChanges) {
    const writes = writesByClass?.[targetClassId] || [];
    const updates = updatesByClass?.[targetClassId] || [];
    const orderMergeSegments = orderMergeSegmentsByClass?.[targetClassId] || [];
    if (!writes.length && !updates.length && !orderMergeSegments.length) continue;

    const targetClass = classById.get(targetClassId);
    const currentOrder = targetClass?.contentBoxesOrder || [];

    try {
      const result = await persistClassCopyBatch({
        db,
        userId,
        targetClassId,
        writes,
        updates,
        currentOrder,
        orderMergeSegments,
      });
      changedDocCount += result.createdIds.length + updates.length;
      undoClasses.push({
        classId: targetClassId,
        createdIds: result.createdIds,
        updates,
        previousOrder: result.previousOrder,
        nextOrder: result.nextOrder,
      });
      classById.set(targetClassId, {
        ...(targetClass || { classId: targetClassId }),
        classId: targetClassId,
        contentBoxesOrder: result.nextOrder,
      });
      updatedClasses.push(classById.get(targetClassId));
    } catch (error) {
      console.error("Failed to persist calendar copy batch:", error);
      return {
        ok: false,
        partialWrite: changedDocCount > 0,
        updatedClasses,
        error,
      };
    }
  }

  return {
    ok: true,
    updatedClasses,
    undoClasses,
  };
}

export function buildCalendarCopyUndoSnapshot({
  activeDateMode = CALENDAR_COPY_ACTIVE_DATE_MODES.EXPLODE,
  undoClasses = [],
  nextOverrides = {},
  affectedDayKeys = [],
  label = "Content boxes copied.",
} = {}) {
  const classes = (undoClasses || [])
    .filter((entry) =>
      entry?.classId &&
      (
        entry.createdIds?.length ||
        entry.updates?.length ||
        entry.previousOrder?.length
      )
    )
    .map((entry) => ({
      classId: entry.classId,
      createdIds: [...new Set((entry.createdIds || []).map(String).filter(Boolean))],
      updatedActiveDates: (entry.updates || [])
        .filter((update) => update?.contentBoxId && update?.previous)
        .map((update) => ({
          contentBoxId: String(update.contentBoxId),
          previousStartDate: update.previous.startDate ?? "",
          previousEndDate: update.previous.endDate ?? update.previous.startDate ?? "",
        })),
      previousOrder: Array.isArray(entry.previousOrder) ? [...entry.previousOrder] : [],
      nextOrder: Array.isArray(entry.nextOrder) ? [...entry.nextOrder] : [],
    }));

  if (!classes.length) {
    return null;
  }

  return {
    kind: "copy",
    activeDateMode,
    label,
    classes,
    localOverrideKeys: Object.keys(nextOverrides || {}),
    affectedDayKeys: [...new Set((affectedDayKeys || []).filter(Boolean))],
    panelLayoutCopies: [],
    createdAt: Date.now(),
  };
}

export async function executeCalendarCopyToDate({
  db,
  userId,
  sourceClassId,
  classes,
  boxes,
  targetDateKey,
  targetClassIds,
  resolveLinkedClassIdsFn,
  createId,
}) {
  if (!userId || !sourceClassId) {
    return {
      ok: false,
      error: CALENDAR_COPY_MISSING_CONTEXT_ERROR,
    };
  }

  if (!Array.isArray(boxes) || boxes.length === 0) {
    return {
      ok: false,
      error: CALENDAR_COPY_ERROR,
    };
  }

  try {
    const plan = await planCalendarCopyToDate({
      boxes,
      targetDateKey,
      targetClassIds,
      sourceClassId,
      userId,
      classes,
      resolveLinkedClassIdsFn,
      createId,
    });

    const persistResult = await persistCalendarCopyPlan({
      db,
      userId,
      classes,
      writesByClass: plan.writesByClass,
    });

    if (!persistResult.ok) {
      return {
        ok: false,
        partialWrite: Boolean(persistResult.partialWrite),
        error: typeof persistResult.error === "string"
          ? persistResult.error
          : resolveCalendarCopyErrorMessage({
              partialWrite: persistResult.partialWrite,
            }),
        updatedClasses: persistResult.updatedClasses || [],
        nextOverrides: {},
      };
    }

    return {
      ok: true,
      layoutCopyEntries: plan.layoutCopyEntries,
      nextOverrides: plan.nextOverrides,
      updatedClasses: persistResult.updatedClasses,
      undoSnapshot: buildCalendarCopyUndoSnapshot({
        undoClasses: persistResult.undoClasses,
        nextOverrides: plan.nextOverrides,
        affectedDayKeys: [targetDateKey],
        label: "Content boxes copied.",
      }),
    };
  } catch (error) {
    console.error("Failed to execute calendar copy:", error);
    return {
      ok: false,
      error: CALENDAR_COPY_ERROR,
    };
  }
}

export async function executeCalendarCopyWithActiveDateMode({
  db,
  userId,
  sourceClassId,
  localClassId = sourceClassId,
  classes,
  copyPairs,
  activeDateMode,
  sourceAnchorDateKey,
  targetAnchorDateKey,
  targetClassIds,
  resolveLinkedClassIdsFn,
  createId,
}) {
  if (!userId || !sourceClassId) {
    return {
      ok: false,
      error: CALENDAR_COPY_MISSING_CONTEXT_ERROR,
    };
  }

  if (!Array.isArray(copyPairs) || copyPairs.length === 0) {
    return {
      ok: false,
      error: CALENDAR_COPY_ERROR,
    };
  }

  try {
    const plan = await planCalendarCopyWithActiveDateMode({
      copyPairs,
      activeDateMode,
      sourceAnchorDateKey,
      targetAnchorDateKey,
      targetClassIds,
      sourceClassId,
      localClassId,
      userId,
      classes,
      resolveLinkedClassIdsFn,
      createId,
    });

    const hasPlannedChanges =
      Object.values(plan.writesByClass || {}).some((writes) => writes.length > 0) ||
      Object.values(plan.updatesByClass || {}).some((updates) => updates.length > 0);
    if (!hasPlannedChanges) {
      return {
        ok: false,
        error: CALENDAR_COPY_NOTHING_TO_COPY_ERROR,
        updatedClasses: [],
        nextOverrides: {},
      };
    }

    const persistResult = await persistCalendarCopyPlan({
      db,
      userId,
      classes,
      writesByClass: plan.writesByClass,
      updatesByClass: plan.updatesByClass,
      orderMergeSegmentsByClass: plan.orderMergeSegmentsByClass,
    });

    if (!persistResult.ok) {
      return {
        ok: false,
        partialWrite: Boolean(persistResult.partialWrite),
        error: typeof persistResult.error === "string"
          ? persistResult.error
          : resolveCalendarCopyErrorMessage({
              partialWrite: persistResult.partialWrite,
            }),
        updatedClasses: persistResult.updatedClasses || [],
        nextOverrides: {},
      };
    }

    return {
      ok: true,
      layoutCopyEntries: plan.layoutCopyEntries,
      nextOverrides: plan.nextOverrides,
      updatedClasses: persistResult.updatedClasses,
      undoSnapshot: buildCalendarCopyUndoSnapshot({
        activeDateMode,
        undoClasses: persistResult.undoClasses,
        nextOverrides: plan.nextOverrides,
        affectedDayKeys: copyPairs.map((pair) => pair?.targetDateKey).filter(Boolean),
        label: "Content boxes copied.",
      }),
    };
  } catch (error) {
    console.error("Failed to execute active-date-aware calendar copy:", error);
    return {
      ok: false,
      error: CALENDAR_COPY_ERROR,
    };
  }
}

export function applyCalendarCopyReduxUpdates(dispatch, setSingleClassAction, updatedClasses = []) {
  updatedClasses.forEach((classData) => {
    if (!classData?.classId) return;
    dispatch(setSingleClassAction(classData));
  });
}
